/* eslint-disable no-use-before-define */

import {
    atoms,
    AButton,
    ACheckbox,
    AHeading,
    AHeadingRich,
    AHtml,
    AIcon,
    AImage,
    AInput,
    ALink,
    ARichText,
    ASelect,
    ATextarea,
    ATextMultiLine,
    ATextSingleLine,
    ALabel,
    AVideo,
    Layout,
    Organism,
    Page,
    Rendering,
    AShowOnMap,
    Facet,
    ARadioButton,
} from '@/data/data-models';

const publicMode = process.env.VUE_APP_ENV === 'public';

/**
 * Field Names
 */

const fieldNames = {
    // Page
    ARTICLE_SUMMARY: 'Article Summary',
    ARTICLE_SUMMARY_PROP: 'articleSummary',
    MAIN_TITLE: 'Main Title',
    MAIN_TITLE_PROP: 'mainTitle',
    BROWSER_TITLE: 'BrowserTitle',
    BROWSER_TITLE_PROP: 'browserTitle',
    META_DESCRIPTION: 'MetaDescription',
    META_DESCRIPTION_PROP: 'metaDescription',
    META_KEYWORDS: 'MetaKeywords',
    META_KEYWORDS_PROP: 'metaKeywords',
    NO_INDEX: 'NoIndex',
    NO_INDEX_PROP: 'noIndex',
    NO_FOLLOW: 'NoFollow',
    NO_FOLLOW_PROP: 'noFollow',
    NO_ARCHIVE: 'NoArchive',
    NO_ARCHIVE_PROP: 'noArchive',
    NO_SNIPPET: 'NoSnippet',
    NO_SNIPPET_PROP: 'noSnippet',
    IS_TEMPLATE: 'IsTemplate',
    IS_TEMPLATE_PROP: 'isTemplate',
    IS_PROTOTYPE: 'Is Prototype',
    IS_PROTOTYPE_PROP: 'isPrototype',
    TAGS_PROP: 'tags',
    TAGS: 'Tags',

    // General
    EDITOR_NAME: 'Editor name',
    EDITOR_NAME_PROP: 'editorName',
    CUSTOM_ATTRIBUTES: 'Custom Attributes',
    CUSTOM_ATTRIBUTES_PROP: 'customAttributes',
    HTML_CUSTOM_ATTRIBUTES: 'HTML Custom Attributes',
    HTML_CUSTOM_ATTRIBUTES_PROP: 'htmlCustomAttributes',
    FE_NAME: 'FE Name',
    FE_NAME_PROP: 'feName',
    IS_LINK: 'Is Link',
    IS_LINK_PROP: 'isLink',
    LINKED_COMPONENT: 'Linked Component',
    LINKED_COMPONENT_PROP: 'linkedComponent',
    LOCK_COMPONENT: 'Lock Component',
    LOCK_COMPONENT_PROP: 'lockComponent',
    GLOBAL_CONTENT: 'Is Global Content',
    GLOBAL_CONTENT_PROP: 'globalContent',
    POINTER: 'Pointer',
    POINTER_PROP: 'pointer',
    USE_CONTEXT_ITEM_FOR_DATASOURCE: 'Use Context Item for datasource',
    USE_CONTEXT_ITEM_FOR_DATASOURCE_PROP: 'useContext',
    LINKED_VALUE_FIELD: 'Linked Value Field',
    LINKED_VALUE_FIELD_PROP: 'linkedValueField',
    ARIA_LABEL: 'aria-label',
    ARIA_LABEL_PROP: 'ariaLabel',
    ARIA_LABELLED_BY: 'aria-labelledby',
    ARIA_LABELLED_BY_PROP: 'ariaLabelledBy',
    ACCESSIBILITY_ROLE: 'role',
    ACCESSIBILITY_ROLE_PROP: 'accessibilityRole',
    ARIA_HIDDEN: 'aria-hidden',
    ARIA_HIDDEN_PROP: 'ariaHidden',

    // Base
    ID_OVERRIDE: 'Id Override',
    ID_OVERRIDE_PROP: 'idOverride',
    CLASSES: 'Classes',
    CLASSES_PROP: 'classes',

    // Modifiers
    TAG: 'Tag',
    TAG_PROP: 'tag',

    // Text
    SINGLE_LINE_TEXT: 'Single-Line Text',
    SINGLE_LINE_TEXT_PROP: 'singleLineText',
    MULTI_LINE_TEXT: 'Multi-Line Text',
    MULTI_LINE_TEXT_PROP: 'multiLineText',
    RICH_TEXT: 'Rich Text',
    RICH_TEXT_PROP: 'richText',
    LABEL_FOR: 'Label For',
    LABEL_FOR_PROP: 'labelFor',

    // Icon
    SET: 'Set',
    SET_PROP: 'set',
    ICON: 'Icon',
    ICON_PROP: 'icon',
    SET_ACTIVE: 'Set Active',
    SET_ACTIVE_PROP: 'setActive',
    ICON_ACTIVE: 'Icon Active',
    ICON_ACTIVE_PROP: 'iconActive',

    // Image
    IMAGE: 'Image',
    IMAGE_PROP: 'image',

    // Button
    TYPE: 'type',
    TYPE_PROP: 'type',

    // Form
    INPUT_NAME: 'Input Name',
    INPUT_NAME_PROP: 'inputName',
    INPUT_TYPE: 'Input Type',
    INPUT_TYPE_PROP: 'inputType',
    INPUT_VALUE: 'Input Value',
    INPUT_VALUE_PROP: 'inputValue',
    PLACEHOLDER: 'Input Placeholder',
    PLACEHOLDER_PROP: 'placeholder',
    OPTIONS: 'Input Options',
    OPTIONS_PROP: 'options',
    PATTERN: 'Pattern',
    PATTERN_PROP: 'pattern',
    REQUIRED: 'Input Required',
    REQUIRED_PROP: 'required',
    VALIDATION_MESSAGE: 'Validation Message',
    VALIDATION_MESSAGE_PROP: 'validationMessage',

    // Link
    LINK: 'Link',
    LINK_PROP: 'link',

    // Coordinates
    COORDINATES: 'Coordinates',
    COORDINATES_PROP: 'coordinates',
};

const propToFieldMap = new Map([
    // Page
    [fieldNames.ARTICLE_SUMMARY_PROP, fieldNames.ARTICLE_SUMMARY],
    [fieldNames.MAIN_TITLE_PROP, fieldNames.MAIN_TITLE],
    [fieldNames.BROWSER_TITLE_PROP, fieldNames.BROWSER_TITLE],
    [fieldNames.META_DESCRIPTION_PROP, fieldNames.META_DESCRIPTION],
    [fieldNames.META_KEYWORDS_PROP, fieldNames.META_KEYWORDS],
    [fieldNames.NO_INDEX_PROP, fieldNames.NO_INDEX],
    [fieldNames.NO_FOLLOW_PROP, fieldNames.NO_FOLLOW],
    [fieldNames.NO_ARCHIVE_PROP, fieldNames.NO_ARCHIVE],
    [fieldNames.NO_SNIPPET_PROP, fieldNames.NO_SNIPPET],
    [fieldNames.IS_TEMPLATE_PROP, fieldNames.IS_TEMPLATE],
    [fieldNames.IS_PROTOTYPE_PROP, fieldNames.IS_PROTOTYPE],
    [fieldNames.TAGS_PROP, fieldNames.TAGS],

    // General
    [fieldNames.EDITOR_NAME_PROP, fieldNames.EDITOR_NAME],
    [fieldNames.CUSTOM_ATTRIBUTES_PROP, fieldNames.CUSTOM_ATTRIBUTES],
    [fieldNames.HTML_CUSTOM_ATTRIBUTES_PROP, fieldNames.HTML_CUSTOM_ATTRIBUTES],
    [fieldNames.FE_NAME_PROP, fieldNames.FE_NAME],
    [fieldNames.IS_LINK_PROP, fieldNames.IS_LINK],
    [fieldNames.LINKED_COMPONENT_PROP, fieldNames.LINKED_COMPONENT],
    [fieldNames.LOCK_COMPONENT_PROP, fieldNames.LOCK_COMPONENT],
    [fieldNames.GLOBAL_CONTENT_PROP, fieldNames.GLOBAL_CONTENT],
    [fieldNames.POINTER_PROP, fieldNames.POINTER],
    [fieldNames.ARIA_LABEL_PROP, fieldNames.ARIA_LABEL],
    [fieldNames.ARIA_LABELLED_BY_PROP, fieldNames.ARIA_LABELLED_BY],
    [fieldNames.ACCESSIBILITY_ROLE_PROP, fieldNames.ACCESSIBILITY_ROLE],
    [fieldNames.ARIA_HIDDEN_PROP, fieldNames.ARIA_HIDDEN],

    // Base
    [fieldNames.ID_OVERRIDE_PROP, fieldNames.ID_OVERRIDE],
    [fieldNames.CLASSES_PROP, fieldNames.CLASSES],
    [fieldNames.LINKED_VALUE_FIELD_PROP, fieldNames.LINKED_VALUE_FIELD],
    [fieldNames.USE_CONTEXT_ITEM_FOR_DATASOURCE_PROP, fieldNames.USE_CONTEXT_ITEM_FOR_DATASOURCE],

    // Modifiers
    [fieldNames.TAG_PROP, fieldNames.TAG],

    // Text
    [fieldNames.SINGLE_LINE_TEXT_PROP, fieldNames.SINGLE_LINE_TEXT],
    [fieldNames.MULTI_LINE_TEXT_PROP, fieldNames.MULTI_LINE_TEXT],
    [fieldNames.RICH_TEXT_PROP, fieldNames.RICH_TEXT],
    [fieldNames.LABEL_FOR_PROP, fieldNames.LABEL_FOR],

    // Icon
    [fieldNames.ICON_PROP, fieldNames.ICON],
    [fieldNames.ICON_ACTIVE_PROP, fieldNames.ICON_ACTIVE],
    [fieldNames.SET_PROP, fieldNames.SET],
    [fieldNames.SET_ACTIVE_PROP, fieldNames.SET_ACTIVE],

    // Image
    [fieldNames.IMAGE_PROP, fieldNames.IMAGE],

    // Button
    [fieldNames.TYPE_PROP, fieldNames.TYPE],

    // Input
    [fieldNames.INPUT_NAME_PROP, fieldNames.INPUT_NAME],
    [fieldNames.INPUT_VALUE_PROP, fieldNames.INPUT_VALUE],
    [fieldNames.INPUT_TYPE_PROP, fieldNames.INPUT_TYPE],
    [fieldNames.OPTIONS_PROP, fieldNames.OPTIONS],
    [fieldNames.PLACEHOLDER_PROP, fieldNames.PLACEHOLDER],
    [fieldNames.PATTERN_PROP, fieldNames.PATTERN],
    [fieldNames.REQUIRED_PROP, fieldNames.REQUIRED],
    [fieldNames.VALIDATION_MESSAGE_PROP, fieldNames.VALIDATION_MESSAGE],

    // Link
    [fieldNames.LINK_PROP, fieldNames.POINTER],

    // Coordinates
    [fieldNames.COORDINATES_PROP, fieldNames.COORDINATES],
]);

/**
 * Field Types
 */

const fieldTypes = {
    SINGLE_LINE_TEXT: 'Single-Line Text',
    MULTI_LINE_TEXT: 'Multi-Line Text',
    RICH_TEXT: 'Rich Text',
    NUMBER: 'Number',
    CHECKBOX: 'Checkbox',
    IMAGE: 'Image',
    GENERAL_LINK: 'General Link',
    NAME_VALUE_LIST: 'Name Value List',
    DROP_TREE: 'Droptree',
    TREELIST: 'Multilist',
    DROPLIST: 'Droplist',

    // Unused (move up if needed)
    // DROPLINK: 'Droplink',
    // MULTILIST: 'Multilist',
};

const fieldGroups = {
    STRING_FIELDS: 'StringFields',
    IMAGE_FIELDS: 'ImageFields',
    LINK_FIELDS: 'LinkFields',
};

// All fields in Sitecore are StringFields
// except certain exceptions (Fields stored in
// Sitecore as XML like image) where we switch
// them to objects
const fieldTypeMap = new Map([
    // REQUIRED fields - throws error if unavailable
    [fieldNames.IS_TEMPLATE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
        isRequired: true,
    }],

    [fieldNames.POINTER, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.NUMBER,
        isRequired: true,
    }],
    // REQUIRED fields end

    // Universal fields - not required
    [fieldNames.ID_OVERRIDE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.EDITOR_NAME, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.CUSTOM_ATTRIBUTES, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.NAME_VALUE_LIST,
    }],

    [fieldNames.HTML_CUSTOM_ATTRIBUTES, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.NAME_VALUE_LIST,
    }],

    [fieldNames.FE_NAME, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.IS_LINK, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
    }],

    [fieldNames.USE_CONTEXT_ITEM_FOR_DATASOURCE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
    }],

    [fieldNames.LINKED_VALUE_FIELD, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.DROP_TREE,
    }],

    [fieldNames.LINKED_COMPONENT, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.DROP_TREE,
    }],

    [fieldNames.LOCK_COMPONENT, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
    }],

    [fieldNames.GLOBAL_CONTENT, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
    }],
    // Universal fields end

    [fieldNames.ARTICLE_SUMMARY, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.MULTI_LINE_TEXT,
    }],

    [fieldNames.MAIN_TITLE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.BROWSER_TITLE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.META_DESCRIPTION, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.MULTI_LINE_TEXT,
    }],

    [fieldNames.META_KEYWORDS, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.MULTI_LINE_TEXT,
    }],

    [fieldNames.NO_INDEX, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
    }],

    [fieldNames.NO_FOLLOW, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
    }],

    [fieldNames.NO_ARCHIVE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
    }],

    [fieldNames.NO_SNIPPET, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
    }],

    [fieldNames.IS_PROTOTYPE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
    }],

    [fieldNames.CLASSES, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.TAG, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.TAGS, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.TREELIST,
    }],

    [fieldNames.SINGLE_LINE_TEXT, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.LABEL_FOR, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.MULTI_LINE_TEXT, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.MULTI_LINE_TEXT,
    }],

    [fieldNames.RICH_TEXT, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.RICH_TEXT,
    }],

    [fieldNames.IMAGE, {
        fieldGroup: fieldGroups.IMAGE_FIELDS,
        fieldType: fieldTypes.IMAGE,
    }],

    [fieldNames.LINK, {
        fieldGroup: fieldGroups.LINK_FIELDS,
        fieldType: fieldTypes.GENERAL_LINK,
    }],

    [fieldNames.TYPE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.INPUT_NAME, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.INPUT_TYPE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.INPUT_VALUE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.OPTIONS, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.NAME_VALUE_LIST,
    }],

    [fieldNames.PATTERN, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.PLACEHOLDER, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.REQUIRED, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
    }],

    [fieldNames.VALIDATION_MESSAGE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.SET, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.SET_ACTIVE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.ICON, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.ICON_ACTIVE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.COORDINATES, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.DATE_FORMAT, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.ARIA_LABEL, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.ARIA_LABELLED_BY, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.SINGLE_LINE_TEXT,
    }],

    [fieldNames.ACCESSIBILITY_ROLE, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.DROPLIST,
    }],

    [fieldNames.ARIA_HIDDEN, {
        fieldGroup: fieldGroups.STRING_FIELDS,
        fieldType: fieldTypes.CHECKBOX,
    }],
]);

/**
 * Field functions
 */

function getFieldVal(fields, fieldName) {
    const fieldType = fieldTypeMap.get(fieldName);
    if (!fieldType) {
        console.error(`Field name "${ fieldName }" not added to a field type. Add a mapping to the fieldTypeMap object.`);
        return '';
    }
    if (!fields[fieldType.fieldGroup] || !fields[fieldType.fieldGroup][fieldName]) {
        if (fieldType.isRequired) {
            throw new Error(`sitecore-data-interface.js: Missing required field ${ fieldName }`);
        }

        if (fieldType.fieldType === fieldTypes.IMAGE || fieldType.fieldType === fieldTypes.GENERAL_LINK) {
            return {};
        }

        return ''; // all other types are strings
    }

    const field = fields[fieldType.fieldGroup][fieldName];
    let fieldVal;

    switch (fieldType.fieldType) {
        case fieldTypes.CHECKBOX: {
            fieldVal = field.value.toLowerCase() === 'true'; // convert string to bool
            break;
        }

        case fieldTypes.NUMBER: {
            fieldVal = parseInt(field.value, 10);
            break;
        }

        case fieldTypes.NAME_VALUE_LIST: {
            fieldVal = {};
            if (field.value) {
                const keyVals = field.value.split('&');

                keyVals.forEach((keyVal) => {
                    const [key, val] = keyVal.split('=');
                    fieldVal[key] = decodeURIComponent(val); // required to use special chars in value
                });
            }
            break;
        }

        // rich text has a valueCompiled property which contains the compiled sitecore links/media
        case fieldTypes.RICH_TEXT: {
            fieldVal = publicMode ? field.valueCompiled : field.value;
            break;
        }

        default: {
            // no conversion needed for all others
            fieldVal = field.value;
            break;
        }
    }

    return fieldVal;
}

/**
 * Decode page data
 */

export function setLayoutData(rawData) {
    const main = rawData.layout.placeholders.find(ph => ph.name === 'main');

    if (!rawData) {
        throw new Error('sitecore-data-interface.js: Data is missing main placeholder.');
    }

    // Potentially add other renderings (heading, footer)
    return new Layout({
        pageTemplateId: rawData.layout.pageTemplateId,
        pageTemplateName: rawData.layout.pageTemplateName,
        placeholders: {
            main: processRenderings(main.renderings),
        },
    });
}

export function setPageData(rawData) {
    const pageData = new Page({
        id: rawData.pageId,
        itemName: rawData.itemName,
        itemPath: rawData.path,
        language: rawData.language,
        languages: rawData.languages,
        siteName: rawData.site.name,
        workflow: rawData.workflow,
        locked: rawData.locked,
        lockedby: rawData.lockedby,
        fields: rawData.fields,

        articleSummary: getFieldVal(rawData.fields, fieldNames.ARTICLE_SUMMARY),
        mainTitle: getFieldVal(rawData.fields, fieldNames.MAIN_TITLE),
        browserTitle: getFieldVal(rawData.fields, fieldNames.BROWSER_TITLE),
        metaDescription: getFieldVal(rawData.fields, fieldNames.META_DESCRIPTION),
        metaKeywords: getFieldVal(rawData.fields, fieldNames.META_KEYWORDS),
        noIndex: getFieldVal(rawData.fields, fieldNames.NO_INDEX),
        noFollow: getFieldVal(rawData.fields, fieldNames.NO_FOLLOW),
        noArchive: getFieldVal(rawData.fields, fieldNames.NO_ARCHIVE),
        noSnippet: getFieldVal(rawData.fields, fieldNames.NO_SNIPPET),

        isTemplate: getFieldVal(rawData.fields, fieldNames.IS_TEMPLATE),
        isPrototype: getFieldVal(rawData.fields, fieldNames.IS_PROTOTYPE),

        tags: getFieldVal(rawData.fields, fieldNames.TAGS),
    });

    return pageData;
}

export function processRenderings(renderings) {
    const processedRenderings = [];

    renderings.forEach((r, i) => {
        if (!r.dataSource) {
            // in case of bad data source, return and continue rendering others
            return;
        }

        const rDataSource = r.dataSource[0].name ? r.dataSource[0] : r.dataSource[0].datasource;
        const elementType = rDataSource.name.split('_')[0];
        // Renderings are "Organisms"
        const rendering = new Rendering({
            id: r.uid, // converted to id for instance ID for consistency in FE
            idOverride: getFieldVal(rDataSource.fields, fieldNames.ID_OVERRIDE),
            renderingId: r.id,
            renderingName: r.name,
            renderingField: r.renderingField,
            linkedValueField: getFieldVal(rDataSource.fields, fieldNames.LINKED_VALUE_FIELD),
            useContext: getFieldVal(rDataSource.fields, fieldNames.USE_CONTEXT_ITEM_FOR_DATASOURCE) === true,
            linkedComponent: getFieldVal(rDataSource.fields, fieldNames.LINKED_COMPONENT),
            lockComponent: getFieldVal(rDataSource.fields, fieldNames.LOCK_COMPONENT) === true,
            globalContent: getFieldVal(rDataSource.fields, fieldNames.GLOBAL_CONTENT) === true,
            editorName: getFieldVal(rDataSource.fields, fieldNames.EDITOR_NAME),
            dataSourceId: rDataSource.id,
            name: rDataSource.name,
            elementType,
            templateId: rDataSource.templateId,
            templateType: rDataSource.templateType,
            pointer: i + 1,
            classes: getFieldVal(rDataSource.fields, fieldNames.CLASSES),
            customAttributes: getFieldVal(rDataSource.fields, fieldNames.CUSTOM_ATTRIBUTES) || {},
            htmlCustomAttributes: getFieldVal(rDataSource.fields, fieldNames.HTML_CUSTOM_ATTRIBUTES) || {},
            elements: processDataSources(rDataSource.dataSource),
            ariaLabel: getFieldVal(rDataSource.fields, fieldNames.ARIA_LABEL),
            ariaLabelledBy: getFieldVal(rDataSource.fields, fieldNames.ARIA_LABELLED_BY),
            accessibilityRole: getFieldVal(rDataSource.fields, fieldNames.ACCESSIBILITY_ROLE),
            ariaHidden: getFieldVal(rDataSource.fields, fieldNames.ARIA_HIDDEN) === true,
            tag: getFieldVal(rDataSource.fields, fieldNames.TAG),
        });

        if (rDataSource.renderingId) {
            rendering.renderingId = rDataSource.renderingId;
        }

        processedRenderings.push(rendering);
    });

    return processedRenderings;
}

export function processDataSources(dataSources) {
    const processedDataSources = [];
    if (!dataSources) {
        return processedDataSources;
    }
    dataSources.forEach((ds, i) => {
        const name = ds.name;
        const elementType = name.split('_')[0]; // 'a-heading_2' split to get type
        const link = getFieldVal(ds.fields, fieldNames.LINK);
        const baseElementData = {
            id: ds.id,
            name: ds.name,
            idOverride: getFieldVal(ds.fields, fieldNames.ID_OVERRIDE),
            classes: getFieldVal(ds.fields, fieldNames.CLASSES),
            customAttributes: getFieldVal(ds.fields, fieldNames.CUSTOM_ATTRIBUTES) || {},
            htmlCustomAttributes: getFieldVal(ds.fields, fieldNames.HTML_CUSTOM_ATTRIBUTES) || {},
            linkedValueField: getFieldVal(ds.fields, fieldNames.LINKED_VALUE_FIELD),
            useContext: getFieldVal(ds.fields, fieldNames.USE_CONTEXT_ITEM_FOR_DATASOURCE) === true,
            editorName: getFieldVal(ds.fields, fieldNames.EDITOR_NAME),
            elementType,
            globalContent: getFieldVal(ds.fields, fieldNames.GLOBAL_CONTENT) === true,
            isLink: getFieldVal(ds.fields, fieldNames.IS_LINK) === true,
            linkedComponent: getFieldVal(ds.fields, fieldNames.LINKED_COMPONENT),
            lockComponent: getFieldVal(ds.fields, fieldNames.LOCK_COMPONENT) === true,
            pointer: i + 1,
            renderingField: ds.renderingField,
            renderingId: ds.renderingId,
            templateId: ds.templateId,
            templateType: ds.templateType,
            workflow: ds.workflow,
            ariaLabel: getFieldVal(ds.fields, fieldNames.ARIA_LABEL),
            ariaLabelledBy: getFieldVal(ds.fields, fieldNames.ARIA_LABELLED_BY),
            accessibilityRole: getFieldVal(ds.fields, fieldNames.ACCESSIBILITY_ROLE),
            ariaHidden: getFieldVal(ds.fields, fieldNames.ARIA_HIDDEN) === true,
            tag: getFieldVal(ds.fields, fieldNames.TAG),

            // link data - some vals below may be undefined
            href: link.href,
            anchor: link.anchor,
            linkType: link.linkType,
            title: link.title,
            target: link.target,
            queryString: link.queryString || '',
            mediaId: link.id,

            // form data
            fieldName: getFieldVal(ds.fields, fieldNames.EDITOR_NAME),
            pattern: getFieldVal(ds.fields, fieldNames.PATTERN),
            required: getFieldVal(ds.fields, fieldNames.REQUIRED) === true,
            inputType: getFieldVal(ds.fields, fieldNames.INPUT_TYPE),
        };

        let dataSource;
        switch (elementType) {
            case atoms.BUTTON: {
                const elementSpecificData = {
                    singleLineText: getFieldVal(ds.fields, fieldNames.SINGLE_LINE_TEXT),
                    type: getFieldVal(ds.fields, fieldNames.TYPE),
                };

                dataSource = new AButton({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.CHECKBOX: {
                const elementSpecificData = {
                    inputName: getFieldVal(ds.fields, fieldNames.INPUT_NAME),
                    required: getFieldVal(ds.fields, fieldNames.REQUIRED),
                    validationMessage: getFieldVal(ds.fields, fieldNames.VALIDATION_MESSAGE),
                };

                dataSource = new ACheckbox({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.RADIO_BUTTON: {
                const elementSpecificData = {
                    inputName: getFieldVal(ds.fields, fieldNames.INPUT_NAME),
                    required: getFieldVal(ds.fields, fieldNames.REQUIRED),
                    validationMessage: getFieldVal(ds.fields, fieldNames.VALIDATION_MESSAGE),
                };

                dataSource = new ARadioButton({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.HEADING: {
                const elementSpecificData = {
                    singleLineText: getFieldVal(ds.fields, fieldNames.SINGLE_LINE_TEXT),
                    tag: getFieldVal(ds.fields, fieldNames.TAG),
                };

                dataSource = new AHeading({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.HEADING_RICH: {
                const elementSpecificData = {
                    richText: getFieldVal(ds.fields, fieldNames.RICH_TEXT),
                    tag: getFieldVal(ds.fields, fieldNames.TAG),
                };

                dataSource = new AHeadingRich({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.HTML: {
                const elementSpecificData = {
                    richText: getFieldVal(ds.fields, fieldNames.RICH_TEXT),
                };

                dataSource = new AHtml({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.ICON: {
                const elementSpecificData = {
                    set: getFieldVal(ds.fields, fieldNames.SET),
                    setActive: getFieldVal(ds.fields, fieldNames.SET_ACTIVE),
                    icon: getFieldVal(ds.fields, fieldNames.ICON),
                    iconActive: getFieldVal(ds.fields, fieldNames.ICON_ACTIVE),
                };

                dataSource = new AIcon({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.IMAGE: {
                const image = getFieldVal(ds.fields, fieldNames.IMAGE);
                const elementSpecificData = {
                    // some vals may be undefined
                    src: image.src,
                    alt: image.alt,
                    width: image.width,
                    height: image.height,
                    mediaId: image.mediaId,
                    chId: image.chId,
                };

                dataSource = new AImage({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.INPUT: {
                const elementSpecificData = {
                    inputName: getFieldVal(ds.fields, fieldNames.INPUT_NAME),
                    pattern: getFieldVal(ds.fields, fieldNames.PATTERN),
                    placeholder: getFieldVal(ds.fields, fieldNames.PLACEHOLDER),
                    required: getFieldVal(ds.fields, fieldNames.REQUIRED),
                    inputType: getFieldVal(ds.fields, fieldNames.INPUT_TYPE),
                    validationMessage: getFieldVal(ds.fields, fieldNames.VALIDATION_MESSAGE),
                    inputValue: getFieldVal(ds.fields, fieldNames.INPUT_VALUE),
                };

                dataSource = new AInput({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.LINK: {
                const elementSpecificData = {
                    singleLineText: getFieldVal(ds.fields, fieldNames.SINGLE_LINE_TEXT),
                };

                dataSource = new ALink({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.RICH_TEXT: {
                const elementSpecificData = {
                    richText: getFieldVal(ds.fields, fieldNames.RICH_TEXT),
                };

                dataSource = new ARichText({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.SELECT: {
                const elementSpecificData = {
                    inputName: getFieldVal(ds.fields, fieldNames.INPUT_NAME),
                    options: getFieldVal(ds.fields, fieldNames.OPTIONS) || {},
                    placeholder: getFieldVal(ds.fields, fieldNames.PLACEHOLDER),
                    required: getFieldVal(ds.fields, fieldNames.REQUIRED),
                    validationMessage: getFieldVal(ds.fields, fieldNames.VALIDATION_MESSAGE),
                };

                dataSource = new ASelect({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.TEXTAREA: {
                const elementSpecificData = {
                    inputName: getFieldVal(ds.fields, fieldNames.INPUT_NAME),
                    placeholder: getFieldVal(ds.fields, fieldNames.PLACEHOLDER),
                    required: getFieldVal(ds.fields, fieldNames.REQUIRED),
                    validationMessage: getFieldVal(ds.fields, fieldNames.VALIDATION_MESSAGE),
                };

                dataSource = new ATextarea({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.TEXT_MULTI_LINE: {
                const elementSpecificData = {
                    multiLineText: getFieldVal(ds.fields, fieldNames.MULTI_LINE_TEXT),
                    tag: getFieldVal(ds.fields, fieldNames.TAG),
                };

                dataSource = new ATextMultiLine({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.LABEL: {
                const elementSpecificData = {
                    singleLineText: getFieldVal(ds.fields, fieldNames.SINGLE_LINE_TEXT),
                    labelFor: getFieldVal(ds.fields, fieldNames.LABEL_FOR),
                };

                dataSource = new ALabel({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.TEXT_SINGLE_LINE: {
                const elementSpecificData = {
                    singleLineText: getFieldVal(ds.fields, fieldNames.SINGLE_LINE_TEXT),
                    tag: getFieldVal(ds.fields, fieldNames.TAG),
                };

                dataSource = new ATextSingleLine({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.VIDEO: {
                const elementSpecificData = {
                    richText: getFieldVal(ds.fields, fieldNames.RICH_TEXT),
                };

                dataSource = new AVideo({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.FACETSELECT:
            case atoms.FACETLIST: {
                const elementSpecificData = {
                    tag: getFieldVal(ds.fields, fieldNames.TAG),
                };

                dataSource = new Facet({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.SHOWONMAP: {
                const elementSpecificData = {
                    coordinates: getFieldVal(ds.fields, fieldNames.COORDINATES),
                };

                dataSource = new AShowOnMap({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            case atoms.DATE: {
                const elementSpecificData = {
                    singleLineText: getFieldVal(ds.fields, fieldNames.SINGLE_LINE_TEXT),
                    tag: getFieldVal(ds.fields, fieldNames.TAG),
                    dateField: getFieldVal(ds.fields, fieldNames.DATE_FORMAT),
                };

                dataSource = new ATextSingleLine({
                    ...baseElementData,
                    ...elementSpecificData,
                });

                break;
            }

            default:
                // here are where all complex organisms are processed
                // process their child elements
                dataSource = new Organism({
                    ...baseElementData,
                    elements: processDataSources(ds.dataSource),
                });
                break;
        }

        processedDataSources.push(dataSource);
    });

    return processedDataSources;
}

/**
 * Encode page data
 */

class SitecorePage {
    constructor(data) {
        this.pageId = data.pageId;
        this.itemName = data.itemName;
        this.path = data.path;
        this.database = 'master';
        this.language = data.language;
        this.languages = data.languages;
        this.site = { name: data.siteName };
        this.fields = data.fields;
        this.workflow = data.workflow;
        this.layout = data.layout;
    }
}

class SitecoreLayout {
    constructor(data) {
        this.pageTemplateId = data.pageTemplateId;
        this.pageTemplateName = data.pageTemplateName;
        this.placeholders = data.placeholders;
    }
}

class SitecorePlaceholder {
    constructor(data) {
        this.name = data.name;
        this.path = data.path;
        this.renderings = data.renderings;
    }
}

class SitecoreRendering {
    constructor(data) {
        this.uid = data.uid;
        this.id = data.id;
        this.name = data.name;
        this.renderingField = data.renderingField;
        this.dataSource = data.dataSource;
    }
}

class SitecoreDataSource {
    constructor(data) {
        this.id = data.id;
        this.name = data.name;
        this.displayName = data.name; // just use data.name, duplicate the name field
        this.fields = data.fields;
        this.dataSource = data.dataSource;
        this.workflow = data.workflow;
        this.templateId = data.templateId;
        this.templateType = data.templateType;
        this.renderingField = data.renderingField;
        this.renderingId = data.renderingId;
        this.editorName = data.editorName;
    }
}

class SitecoreField {
    constructor(data) {
        this.value = data.value;
        this.type = data.type;
    }
}

class SitecoreLink {
    constructor(data) {
        this.anchor = data.anchor;
        this.href = data.href;
        this.id = data.id;
        this.linkType = data.linkType;
        this.queryString = data.queryString;
        this.target = data.target;
        this.title = data.title;
    }
}

class SitecoreImage {
    constructor(data) {
        this.src = data.src;
        this.alt = data.alt;
        this.height = data.height;
        this.width = data.width;
        this.mediaId = data.mediaId;
        this.chId = data.chId;
    }
}

export function buildPage(page) {
    return new SitecorePage({
        pageId: page.pageData.id,
        itemName: page.pageData.itemName,
        path: page.pageData.itemPath,
        language: page.pageData.language,
        siteName: page.pageData.siteName,
        fields: propsToFields(page.pageData),
        workflow: page.pageData.workflow,
        layout: buildLayout(page.layout),
    });
}

function buildLayout(layout) {
    const placeholders = [];

    Object.keys(layout.placeholders).forEach((phKey) => {
        const placeholder = new SitecorePlaceholder({
            name: phKey,
            path: phKey,
            renderings: buildRenderings(layout.placeholders[phKey]),
        });

        placeholders.push(placeholder);
    });

    return new SitecoreLayout({
        pageTemplateId: layout.pageTemplateId,
        pageTemplateName: layout.pageTemplateName,
        placeholders,
    });
}

// Rendering with data source as child
export function buildRenderings(renderings) {
    if (!renderings.length) return [];

    const updatedRenderings = [];

    renderings.forEach((rendering) => {
        const updatedRendering = new SitecoreRendering({
            uid: rendering.id,
            id: rendering.renderingId,
            name: rendering.renderingName,
            renderingField: rendering.renderingField,
            dataSource: [new SitecoreDataSource({
                id: rendering.dataSourceId,
                name: rendering.name,
                // only going to add fields registered at top of file
                fields: propsToFields(rendering),
                dataSource: buildDataSources(rendering.elements),
                workflow: rendering.workflow,
                templateId: rendering.templateId,
                templateType: rendering.templateType,
                renderingField: rendering.renderingField,
                renderingId: rendering.renderingId,
                isLink: rendering.isLink,
            })],
        });

        updatedRenderings.push(updatedRendering);
    });

    return updatedRenderings;
}

// building a single rendering (via the Component Editor)
export function buildRendering(rendering, componentData, isNew) {
    const updatedRendering = buildRenderings([rendering])[0];

    updatedRendering.Component = updatedRendering.dataSource[0];
    updatedRendering.Component.name = rendering.elementType;

    if (isNew) {
        updatedRendering.Component.componentCategory = componentData.componentCategory;
        updatedRendering.Component.componentSubcategory = componentData.componentSubcategory;
        updatedRendering.Component.componentType = componentData.componentType;
        updatedRendering.Component.editorName = componentData.editorName;
        // the parent folder
        updatedRendering.Component.id = componentData.id;
    } else {
        // the actual rendering
        updatedRendering.Component.id = rendering.id;
    }

    return updatedRendering;
}

function buildDataSources(elements) {
    if (!elements) return [];

    const dataSources = [];
    elements.forEach((element) => {
        const dataSource = new SitecoreDataSource({
            id: element.id,
            name: element.name,
            fields: propsToFields(element),
            dataSource: buildDataSources(element.elements),
            workflow: element.workflow,
            templateId: element.templateId,
            templateType: element.templateType,
            renderingField: element.renderingField,
            renderingId: element.renderingId,
            editorName: element.editorName,
            isLink: element.isLink,
        });

        dataSources.push(dataSource);
    });

    return dataSources;
}

function propsToFields(element) {
    const fields = {};
    if (
        element.templateType === 'Organism'
        || (element.templateType && element.templateType.startsWith('a-'))
    ) {
        fields[fieldGroups.LINK_FIELDS] = {
            [fieldNames.LINK]: {
                value: new SitecoreLink({
                    anchor: element.anchor,
                    href: element.href,
                    id: element.mediaId,
                    linkType: element.linkType,
                    queryString: element.queryString,
                    target: element.target,
                    title: element.title,
                }),
                type: fieldTypes.GENERAL_LINK,
            },
        };
    }

    if (element.elementType === atoms.IMAGE) {
        fields[fieldGroups.IMAGE_FIELDS] = {
            [fieldNames.IMAGE]: {
                value: new SitecoreImage({
                    src: element.src,
                    alt: element.alt,
                    mediaId: element.mediaId,
                    width: element.width,
                    height: element.height,
                    chId: element.chId,
                }),
                type: fieldTypes.IMAGE,
            },
        };
    }

    Object.keys(element).forEach((propKey) => {
        // **Make sure all properties exist in the map!**
        if (!propToFieldMap.has(propKey)) return;

        const fieldName = propToFieldMap.get(propKey);
        const fieldData = fieldTypeMap.get(fieldName);

        if (!Object.prototype.hasOwnProperty.call(fields, fieldData.fieldGroup)) {
            fields[fieldData.fieldGroup] = {};
        }

        // Convert values back to string, if needed
        let fieldVal;

        if (fieldData.fieldType === fieldTypes.NUMBER
            || fieldData.fieldType === fieldTypes.CHECKBOX) {
            fieldVal = String(element[propKey]);
        } else if (fieldData.fieldType === fieldTypes.NAME_VALUE_LIST) {
            if (element[propKey]) {
                fieldVal = Object.keys(element[propKey])
                    .map(key => `${ key }=${ encodeURIComponent(element[propKey][key]) }`)
                    .join('&');
            } else {
                fieldVal = '';
            }
        } else if (fieldData.fieldType === fieldTypes.TREELIST && propKey === 'tags') {
            if (element[propKey]) {
                const fieldValues = [];
                element[propKey].forEach((value) => {
                    if (value.tags) {
                        value.tags.forEach((v) => {
                            fieldValues.push(v.id);
                        });
                    }
                });
                fieldVal = fieldValues.join('|');
            } else {
                fieldVal = '';
            }
        } else {
            fieldVal = element[propKey];
        }

        fields[fieldData.fieldGroup][fieldName] = new SitecoreField({
            value: fieldVal,
            type: fieldData.fieldType,
        });
    });
    return fields;
}
